import { useState } from "react";

import { CallCenterUserCredentialsWithoutSecret } from "@/Common/Api/CallCenter/Responses/LoginToCallCenterResponse";
import { Client } from "@/Common/Api/Client";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { CallCenterHelper } from "@/Common/Helpers/CallCenterHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { TextboxControl } from "../Inputs/TextboxControl";
import { Popup } from "../Modals/Popup";
import { CallCenterUserCredentialsState, ProviderTypes, UpdateUserCallCenterCredentialsRequest, UpdateUserCallCenterCredentialsResponse } from "./CallCenterSettings.types";

import styles from "./CallCenterLoginPopup.module.scss";

interface ICallCenterLoginPopupProps {
    credentials: CallCenterUserCredentialsWithoutSecret
}

function CallCenterLoginPopup(props: Readonly<ICallCenterLoginPopupProps>): JSX.Element {
    const { getText } = useTranslation();
    const titleText = getText("CallCenter_AccountPopupTitle", { providerType: props.credentials?.Provider });
    const cancelLabel = getText("CancelLabel");
    const submitLabel = getText("CallCenter_AccountPopupSubmit");
    const usernameLabel = getText("CallCenterComUnikUsernameLabel");
    const passwordLabel = getText("CallCenterComUnikPasswordLabel");

    const [saving, setSaving] = useState<boolean>(false);

    const [username, setUsername] = useState<string>(props.credentials?.ExternalUsername ?? null);
    const [password, setPassword] = useState<string>(null);
    const [passwordError, setPasswordError] = useState<string>(null);

    async function saveCredentials(savingCredentials: CallCenterUserCredentialsState): Promise<void> {
        setPasswordError(null);
        if (savingCredentials != null && savingCredentials.Secret == null) {
            // resuse existing account
            CallCenterHelper.closeLoginWindow();
            await CallCenterHelper.loginAsync({ isUserInitiated: true });
            return;
        }

        if (saving) {
            return;
        }
        setSaving(true);

        const requestBody: UpdateUserCallCenterCredentialsRequest = { Credentials: savingCredentials != null ? { ...savingCredentials } : null };
        const response = await Client.postAsync<UpdateUserCallCenterCredentialsResponse>(ServerOperations.Server.Myle_CallCenter_UpdateUserCredentials, requestBody);

        if (!response.isSuccess) {
            // remove secret from state before logging
            const debugState = savingCredentials == null ? null : {
                ...savingCredentials,
                Secret: savingCredentials.Secret != null ? ("was provided with length " + savingCredentials.Secret.length) : "was null"
            };
            console.error("Error Saving CallCenterCredentials.", response.errors, debugState);

            setSaving(false);
            setPasswordError(sessionStore.formatErrorsForDisplay(CallCenterHelper.formatErrorMessages(response.errors, savingCredentials.Provider)).join(", "));
            return;
        }

        CallCenterHelper.closeLoginWindow();
        const savedCredentials = CallCenterUserCredentialsState.fromResponse(response.data.Credentials);

        if (savedCredentials != null) {
            await CallCenterHelper.loginAsync({ isUserInitiated: true });
        }
    }

    const isExistingUsername = username === props.credentials?.ExternalUsername;

    const disabled = saving;

    const isUsernameRequired = password != null;
    const isPasswordRequired = username != null && !isExistingUsername;

    const isUsernameValidEmail = username == null || ValidationHelper.isExactMatch(username, ValidationHelper.emailRegex);
    const isUsernameValidRequired = !isUsernameRequired || username != null;
    const isPasswordValidRequired = !isPasswordRequired || password != null;

    const usernameError = !isUsernameValidEmail ? cacheStore.GetString("EmailHasInvalidFormatLabel") : null;

    const isUsernameValid = isUsernameValidRequired && isUsernameValidEmail;
    const isPasswordValid = isPasswordValidRequired;

    const canSave = isUsernameValid && isPasswordValid;

    const showExistingPasswordPlaceholder = isExistingUsername;

    function trimTextOrNull(text: string): string | null {
        text = text.trim();
        if (text === "") {
            return null;
        }
        return text;
    }

    function onUsernameChanged(text: string): void {
        text = trimTextOrNull(text);
        if (text === username) {
            return;
        }
        setUsername(text);
    }

    function onPasswordChanged(text: string): void {
        text = trimTextOrNull(text);
        if (text === password) {
            return;
        }
        setPassword(text);
    }

    function onSave(): void {
        if (!canSave)
            return;

        let newCredentials = null;
        if (username != null) {
            newCredentials = new CallCenterUserCredentialsState({
                Provider: ProviderTypes.ComUnik,
                ExternalUsername: username,
                Secret: password
            });
        }
        saveCredentials(newCredentials);
    }

    function onCancel(): void {
        CallCenterHelper.closeLoginWindow();
    }

    return (
        <Popup
            Id="CallCenterLoginPopup"
            title={titleText}
            altView={false}
            titleLowerCase={true}
            onCloseDelegate={() => CallCenterHelper.closeLoginWindow()}
        >
            <div className={styles.editCredentialsPopup}>
                <div className={styles.textbox}>
                    <label htmlFor="comUnikUsername">{usernameLabel}</label>
                    <TextboxControl
                        id="comUnikUsername"
                        type="email"
                        value={username}
                        valid={isUsernameValid}
                        onChange={onUsernameChanged}
                        disabled={disabled}
                        maxLength={200}
                        style={{ height: "37px" }}
                    />
                    <small
                        className={styles.errorMessage}
                        style={{ visibility: usernameError == null ? "hidden" : "visible" }}
                    >
                        {usernameError ?? "&nbsp;"}
                    </small>
                </div>
                <div className={styles.textbox}>
                    <label htmlFor="callCenterPassword">{passwordLabel}</label>
                    <TextboxControl
                        id="callCenterPassword"
                        type="password"
                        value={password}
                        valid={isPasswordValid}
                        onChange={onPasswordChanged}
                        disabled={disabled}
                        placeholder={showExistingPasswordPlaceholder ? "••••••••" : ""}
                        style={{ height: "37px" }}
                    />
                    {passwordError && (
                        <small className={styles.errorMessage}>
                            {passwordError}
                        </small>
                    )}
                    <small
                        className={styles.errorMessage}
                        style={{ display: passwordError == null ? "block" : "none" }}
                    >
                        {passwordError}
                    </small>
                </div>
            </div>
            <div className="p-footer">
                <div className="row">
                    <div className="col-sm-12 columns text-right flex-display flex-end flex-gap-xs">
                        <button
                            disabled={disabled}
                            onClick={onCancel}
                            className="btn btn-default"
                        >
                            {cancelLabel}
                        </button>
                        <button
                            disabled={disabled || !(canSave)}
                            onClick={onSave}
                            className="btn btn-primary"
                        >
                            {submitLabel}
                        </button>
                    </div>
                </div>
            </div>
        </Popup>
    );
}

export { CallCenterLoginPopup };
